/**
 * Screen 5 — one wine, every time you have seen it, and what you thought.
 *
 * The sighting timeline is the feature the whole Firestore identity layer
 * exists for: "you took this picture on 4 March" only means something if the
 * app can prove it with the photograph.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'

import Comments from '../components/Comments'
import Inventory from '../components/Inventory'
import Stars from '../components/Stars'
import { api } from '../lib/api'
import { useSession } from '../lib/session'
import type { Sighting } from '../lib/types'

export default function WineDetail() {
  const { wineId } = useParams<{ wineId: string }>()
  const { user } = useSession()
  const client = useQueryClient()
  const [lightbox, setLightbox] = useState<string | null>(null)
  const [selectedPersona, setSelectedPersona] = useState<string>('')
  const [notes, setNotes] = useState<string>('')

  const personasQuery = useQuery({ queryKey: ['personas'], queryFn: api.personas })
  const personas = personasQuery.data?.personas ?? []

  useEffect(() => {
    if (!selectedPersona && personas.length > 0) {
      setSelectedPersona(personas[0].id)
    }
  }, [personas, selectedPersona])

  const query = useQuery({
    queryKey: ['wine', wineId],
    queryFn: () => api.wine(wineId!),
    enabled: Boolean(wineId),
  })

  const rate = useMutation({
    mutationFn: (stars: number) => api.rate(wineId!, stars),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['wine', wineId] })
      // The grid shows your stars, so it is stale the moment you rate.
      void client.invalidateQueries({ queryKey: ['cellar'] })
    },
  })

  const requestReview = useMutation({
    mutationFn: ({ personaId, notes }: { personaId: string; notes?: string }) =>
      api.requestCriticReview(wineId!, personaId, notes),
    onSuccess: () => {
      setNotes('')
      void client.invalidateQueries({ queryKey: ['wine', wineId] })
    },
  })

  if (query.isLoading) {
    return (
      <div className="pt-5">
        <Back />
        <div className="card mt-3 h-72 animate-pulse opacity-40" />
      </div>
    )
  }
  if (query.isError) {
    return (
      <div className="pt-5">
        <Back />
        <p className="pt-12 text-center text-sm text-rose-300">
          {(query.error as Error).message}
        </p>
      </div>
    )
  }

  const {
    wine,
    sightings,
    myRating,
    comments,
    criticReviews = [],
    bottleCount,
  } = query.data!
  const hero = sightings.find((sighting) => sighting.cropUrl)?.cropUrl
  const name =
    (wine.displayName as string) ??
    [wine.producer, wine.cuvee, wine.vintage].filter(Boolean).join(' ')
  const aggregate = (wine.aggregate ?? {}) as { avgRating?: number; ratingCount?: number }
  const palate = wine.palate as Sighting['palate']

  return (
    <div className="space-y-6 pt-5">
      <Back />
      <section className="card overflow-hidden">
        {hero && <img src={hero} alt="" className="h-56 w-full object-cover object-top" />}
        <div className="space-y-2 p-4">
          <h2 className="font-display text-xl leading-tight">{name}</h2>
          <p className="text-[11px] tracking-wide text-stone-500 uppercase">
            {[wine.appellation ?? wine.region, wine.country, wine.colour]
              .filter(Boolean)
              .join(' · ')}
          </p>
          {Array.isArray(wine.grapes) && wine.grapes.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {(wine.grapes as string[]).map((grape) => (
                <span key={grape} className="pill bg-white/5 text-stone-300">
                  {grape}
                </span>
              ))}
            </div>
          )}
          {(wine.summary || wine.styleNote) && (
            <p className="mt-2 text-xs leading-relaxed text-stone-300">
              {(wine.summary as string) ?? (wine.styleNote as string)}
            </p>
          )}
          {palate && <PalateNote palate={palate} />}
        </div>
      </section>

      {/* The whole table, about this bottle — as distinct from the single
          fresh review below, which is one critic looking at it in isolation.
          This one carries it over to the cellar and asks what comes next. */}
      <Link to={`/ask?wine=${wineId}`} className="btn-ghost block w-full text-center">
        Ask the critics about this bottle →
      </Link>

      {typeof bottleCount === 'number' && (
        <section className="card space-y-2 p-4">
          <h3 className="label">How many are left?</h3>
          <p className="text-xs text-stone-500">
            The critics only recommend what you still have.
          </p>
          <Inventory wineId={wineId!} count={bottleCount} className="pt-1" />
        </section>
      )}

      <section className="card space-y-2 p-4">
        <h3 className="label">What did you think?</h3>
        <Stars
          value={myRating?.stars ?? 0}
          busy={rate.isPending}
          onChange={(stars) => rate.mutate(stars)}
        />
        {aggregate.ratingCount ? (
          <p className="text-xs text-stone-500">
            {aggregate.avgRating} average from {aggregate.ratingCount}{' '}
            {aggregate.ratingCount === 1 ? 'person' : 'people'}
          </p>
        ) : (
          <p className="text-xs text-stone-500">Nobody has rated this yet.</p>
        )}
        {rate.isError && (
          <p className="text-xs text-rose-300">{(rate.error as Error).message}</p>
        )}
      </section>

      <section className="card space-y-3 p-4">
        <h3 className="label">Ask a Critic for a Fresh Review</h3>
        <p className="text-xs text-stone-400">
          Select a sommelier to review this bottle alone in isolation as a fresh investigation.
        </p>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {personas.map((persona) => (
            <button
              key={persona.id}
              type="button"
              onClick={() => setSelectedPersona(persona.id)}
              className={`rounded-xl border p-3 text-left transition ${
                (selectedPersona || personas[0]?.id) === persona.id
                  ? 'border-wine-500/80 bg-wine-900/30 text-stone-100'
                  : 'border-white/10 bg-black/20 text-stone-300 hover:border-white/20'
              }`}
            >
              <p className="text-xs font-medium">{persona.name}</p>
              <p className="mt-1 line-clamp-2 text-[10px] text-stone-400">{persona.tagline}</p>
            </button>
          ))}
        </div>

        <div className="space-y-1 pt-1">
          <label htmlFor="critic-notes" className="text-[11px] font-medium text-stone-400">
            Notes or questions for context (optional)
          </label>
          <textarea
            id="critic-notes"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder='e.g., "Thinking of having this with salmon, what do you think?"'
            className="w-full rounded-xl border border-white/10 bg-black/30 p-2.5 text-xs text-stone-200 placeholder:text-stone-600 focus:border-wine-500/50 focus:outline-none"
          />
        </div>

        <button
          type="button"
          disabled={requestReview.isPending}
          onClick={() =>
            requestReview.mutate({
              personaId: selectedPersona || personas[0]?.id || 'classicist',
              notes,
            })
          }
          className="btn-primary w-full"
        >
          {requestReview.isPending
            ? 'Consulting critic...'
            : `Get Fresh Review from ${
                personas.find((p) => p.id === (selectedPersona || personas[0]?.id))?.name ??
                'Critic'
              }`}
        </button>

        {requestReview.isError && (
          <p className="text-xs text-rose-300">{(requestReview.error as Error).message}</p>
        )}
      </section>

      {criticReviews.length > 0 && (
        <section className="space-y-3">
          <h3 className="label">Critic Investigations ({criticReviews.length})</h3>
          <div className="space-y-3">
            {criticReviews.map((review) => (
              <article key={review.reviewId} className="card space-y-2 p-4">
                <div className="flex items-center justify-between">
                  <span className="pill border border-wine-500/40 bg-wine-800/40 text-wine-100">
                    {review.personaName}
                  </span>
                  <span className="text-[11px] text-stone-500">
                    {new Date(review.createdAt).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </span>
                </div>

                {review.notes && (
                  <div className="rounded-lg bg-black/30 p-2 text-xs text-stone-300">
                    <span className="text-stone-500 font-medium">Context: </span>
                    “{review.notes}”
                  </div>
                )}

                <p className="text-xs leading-relaxed whitespace-pre-wrap text-stone-200">
                  {review.text}
                </p>
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="space-y-2">
        <h3 className="label">
          You have seen this {sightings.length} {sightings.length === 1 ? 'time' : 'times'}
        </h3>
        <ol className="space-y-3">
          {sightings.map((sighting) => (
            <Row key={sighting.sightingId} sighting={sighting} onOpen={setLightbox} />
          ))}
        </ol>
      </section>

      <Comments
        wineId={wineId!}
        comments={comments}
        me={user?.uid ?? ''}
        displayName={user?.displayName ?? 'Someone'}
        onChanged={() => void client.invalidateQueries({ queryKey: ['wine', wineId] })}
      />


      {lightbox && (
        <button
          className="fixed inset-0 z-50 grid place-items-center bg-black/90 p-4"
          onClick={() => setLightbox(null)}
        >
          <img src={lightbox} alt="" className="max-h-full max-w-full rounded-xl object-contain" />
        </button>
      )}
    </div>
  )
}

/**
 * Out of here.
 *
 * This screen is the one people share, so it is regularly the first page of the
 * session — arrived at cold, with nothing behind it. `location.key` is
 * `'default'` on exactly that entry, and going back from there leaves the app
 * entirely, so it goes to the cellar instead.
 */
function Back() {
  const navigate = useNavigate()
  const cameFromSomewhere = useLocation().key !== 'default'
  return (
    <button
      className="-ml-1 flex items-center gap-1.5 text-sm text-stone-400 hover:text-stone-200"
      onClick={() => (cameFromSomewhere ? navigate(-1) : navigate('/cellar'))}
    >
      <span aria-hidden>←</span>
      {cameFromSomewhere ? 'Back' : 'Your cellar'}
    </button>
  )
}

const AXIS_LABEL: Record<string, string> = {
  quality: 'Quality',
  value: 'Value',
  drink_now: 'Drink now',
  versatility: 'Versatility',
}

function PalateNote({ palate }: { palate: NonNullable<Sighting['palate']> }) {
  const flavours = palate.primary_flavours ?? palate.primaryFlavours ?? []
  const servingTemp = palate.serving_temp_c ?? palate.servingTempC
  const decant = palate.decant_minutes ?? palate.decantMinutes

  if (!flavours.length && !servingTemp && !decant) return null

  return (
    <div className="flex flex-wrap items-center gap-1.5 pt-2">
      {flavours.map((flavour) => (
        <span key={flavour} className="pill bg-white/5 text-stone-300">
          {flavour}
        </span>
      ))}
      {servingTemp && (
        <span className="pill bg-white/5 text-stone-400">{servingTemp} °C</span>
      )}
      {decant && decant > 0 ? (
        <span className="pill bg-white/5 text-stone-400">decant {decant} min</span>
      ) : null}
    </div>
  )
}

function Row({
  sighting,
  onOpen,
}: {
  sighting: Sighting
  onOpen: (url: string) => void
}) {
  const when = sighting.seenAt ? new Date(sighting.seenAt) : null
  const palate = sighting.palate
  const flavours = palate?.primary_flavours ?? palate?.primaryFlavours ?? []
  const servingTemp = palate?.serving_temp_c ?? palate?.servingTempC
  const decant = palate?.decant_minutes ?? palate?.decantMinutes

  return (
    <li className="card space-y-3 p-3">
      <div className="flex items-start gap-3">
        {sighting.cropUrl && (
          <img
            src={sighting.cropUrl}
            alt=""
            className="h-20 w-14 shrink-0 rounded-lg object-cover"
            loading="lazy"
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <p className="text-sm font-medium">
              {when
                ? when.toLocaleDateString(undefined, {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })
                : 'Some time ago'}
            </p>
            {sighting.scanRunId && (
              <Link
                to={`/scan/${sighting.scanRunId}/results`}
                className="text-[11px] text-wine-400 hover:underline"
              >
                View scan →
              </Link>
            )}
          </div>
          <p className="text-xs text-stone-500">
            {when?.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
            {sighting.rank !== undefined && ` · ranked #${sighting.rank}`}
            {sighting.score !== undefined && ` · ${Math.round(sighting.score)}/100`}
          </p>

          {sighting.oneLiner && (
            <p className="mt-1.5 text-xs italic text-wine-200/90">
              “{sighting.oneLiner}”
            </p>
          )}

          {(sighting.styleNote || sighting.summary) && (
            <p className="mt-1 text-xs leading-relaxed text-stone-300">
              {sighting.styleNote ?? sighting.summary}
            </p>
          )}
        </div>

        {sighting.imageUrl && (
          <button
            onClick={() => onOpen(sighting.imageUrl!)}
            className="chip shrink-0"
            title="The photograph you took"
          >
            photo
          </button>
        )}
      </div>

      {(flavours.length > 0 || servingTemp || (decant && decant > 0)) && (
        <div className="flex flex-wrap items-center gap-1.5 border-t border-white/5 pt-2">
          {flavours.map((flavour) => (
            <span key={flavour} className="pill bg-white/5 text-stone-300">
              {flavour}
            </span>
          ))}
          {servingTemp && (
            <span className="pill bg-white/5 text-stone-400">{servingTemp} °C</span>
          )}
          {decant && decant > 0 ? (
            <span className="pill bg-white/5 text-stone-400">decant {decant} min</span>
          ) : null}
        </div>
      )}

      {sighting.axisScores && <Axes axisScores={sighting.axisScores} />}
    </li>
  )
}

function Axes({ axisScores }: { axisScores: Sighting['axisScores'] }) {
  if (!axisScores) return null
  const entries = Object.entries(axisScores)
  if (!entries.length) return null

  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 border-t border-white/5 pt-2">
      {entries.map(([axis, value]) => (
        <div key={axis} className="flex items-center gap-2">
          <dt className="w-20 shrink-0 truncate text-[10px] tracking-wide text-stone-500 uppercase">
            {AXIS_LABEL[axis] ?? axis.replace(/_/g, ' ')}
          </dt>
          <dd className="h-1 flex-1 overflow-hidden rounded-full bg-white/8">
            <div
              className="h-full rounded-full bg-wine-500"
              style={{ width: `${Math.max(0, Math.min(100, Number(value)))}%` }}
            />
          </dd>
        </div>
      ))}
    </dl>
  )
}

